import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Lock, Mail, User, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [adminExists, setAdminExists] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  useEffect(() => {
    // Check if admin already exists
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API}/admin/check-setup`);
        setAdminExists(response.data.admin_exists);
        setIsLogin(response.data.admin_exists);
      } catch (error) {
        console.error("Error checking setup:", error);
      } finally {
        setCheckingSetup(false);
      }
    };
    
    // Check if already logged in
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin/dashboard");
      return;
    }
    
    checkSetup();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/admin/login" : "/admin/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_info", JSON.stringify(response.data.admin));
      
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      navigate("/admin/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="animate-pulse text-graphite/50">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-12">
          <a href="/" className="font-heading text-2xl tracking-[0.2em] uppercase text-graphite-dark">
            Elise Redfield
          </a>
          <p className="font-body text-sm text-graphite/50 mt-2 tracking-wider uppercase">
            Admin Portal
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-sand/30 p-8 md:p-12" data-testid="admin-login-form">
          <h1 className="font-heading text-2xl tracking-wide uppercase text-graphite-dark mb-2">
            {!adminExists ? "Setup Admin" : isLogin ? "Sign In" : "Create Account"}
          </h1>
          <p className="font-body text-sm text-graphite/50 mb-8">
            {!adminExists 
              ? "Create your admin account to get started"
              : isLogin 
                ? "Enter your credentials to access the dashboard"
                : "Register a new admin account"
            }
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name field (only for registration) */}
            {!isLogin && (
              <div>
                <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block">
                  Full Name
                </label>
                <div className="relative">
                  <User size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-graphite/30" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    required={!isLogin}
                    className="w-full bg-transparent border-b border-graphite/20 focus:border-sand-dark outline-none py-3 pl-8 text-graphite-dark placeholder:text-graphite/30 font-body transition-colors"
                    data-testid="admin-name-input"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block">
                Email Address
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-graphite/30" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="admin@example.com"
                  required
                  className="w-full bg-transparent border-b border-graphite/20 focus:border-sand-dark outline-none py-3 pl-8 text-graphite-dark placeholder:text-graphite/30 font-body transition-colors"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-graphite/30" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full bg-transparent border-b border-graphite/20 focus:border-sand-dark outline-none py-3 pl-8 text-graphite-dark placeholder:text-graphite/30 font-body transition-colors"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-graphite-dark text-bone font-heading tracking-widest uppercase transition-all duration-300 hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 mt-8"
              data-testid="admin-submit-btn"
            >
              {loading ? (
                "Processing..."
              ) : (
                <>
                  {isLogin ? "Sign In" : "Create Account"}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Toggle Login/Register (only if admin exists) */}
          {adminExists && (
            <div className="mt-8 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="font-body text-sm text-graphite/50 hover:text-graphite-dark transition-colors"
              >
                {isLogin ? "Need to create an account?" : "Already have an account? Sign in"}
              </button>
            </div>
          )}
        </div>

        {/* Back to site */}
        <div className="mt-8 text-center">
          <a
            href="/"
            className="font-body text-sm text-graphite/40 hover:text-graphite-dark transition-colors"
          >
            ← Back to website
          </a>
        </div>
      </motion.div>
    </div>
  );
}
